import React, { useRef, useEffect } from "react";

const CANVAS_WIDTH = 3201;
const CANVAS_HEIGHT = 1526;
const GRID_SIZE = 25;

const leadOrder_3ch = [
    ['Ⅰ', 'aVR', 'V1', 'V4'],
    ['Ⅱ', 'aVL', 'V2', 'V5'],
    ['Ⅲ', 'aVF', 'V3', 'V6'],
];

const leadOrder_6ch = [
    ['Ⅰ', 'V1'],
    ['Ⅱ', 'V2'],
    ['Ⅲ', 'V3'],
    ['aVR', 'V4'],
    ['aVL', 'V5'],
    ['aVF', 'V6'],
];

const drawDivideLine = (ctx, x, y, h) => {
    const lineColors = ['#000000', '#9F9F9F', '#000000', '#9F9F9F'];
    const segmentHeight = h / lineColors.length;
    for (let i = 0; i < lineColors.length; i++) {
        ctx.beginPath();
        ctx.strokeStyle = lineColors[i];
        ctx.lineWidth = 2;
        ctx.moveTo(x, y + i * segmentHeight);
        ctx.lineTo(x, y + (i + 1) * segmentHeight);
        ctx.stroke();
    }
};

const drawWave = (ctx, data, x, y, width, height, seconds, type , leadNameOffsetY = 40) => {
    if (!data || data.length === 0) return;
    const sampleCount = Math.round(seconds * 500);
    const sliced = data.slice(0, sampleCount);
    if (sliced.length < 2) return;

    const minVal = Math.min(...sliced);
    const maxVal = Math.max(...sliced);
    const range = maxVal - minVal;

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;

    for (let i = 0; i < sliced.length; i++) {
        const px = x + (width / (sliced.length - 1)) * i;
        let py;
        if (range === 0) {
            py = y + height / 2;
        } else {
            const normalizedVal = (sliced[i] - minVal) / range;
            py = y + height - (normalizedVal * height);
        }
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.restore();    

    // Lead 이름 표기
    ctx.save();
    ctx.font = "500 32px Roboto";
    ctx.fillStyle = "#000";
    ctx.fillText(type, x + 10, y + leadNameOffsetY);
    ctx.restore();
};

const drawGrid = (ctx) => {
    ctx.save();
    ctx.strokeStyle = "#FF8383";
    ctx.lineWidth = 1;
    ctx.translate(0.5, 0.5);

    for (let x = 0; x <= CANVAS_WIDTH; x += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, CANVAS_HEIGHT - 1);
        ctx.stroke();
    }
    for (let y = 0; y < CANVAS_HEIGHT; y += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(CANVAS_WIDTH - 1, y);
        ctx.stroke();
    }
    ctx.restore();
};

const drawFooterText = (ctx, y, format) => {
    ctx.save();
    ctx.font = "500 32px Roboto";
    ctx.fillStyle = "#000";
    ctx.textBaseline = "middle";
    let x = 20;
    let items = [
        "60Hz",
        "0.05Hz ~ 40Hz",
        "10mm/mV",
        "25mm/sec",
        "3*4 1R"
    ];
    if (format === '6ch(6ch*5s)') items[4] = "2*6";
    if (format === '6ch(6ch*5s) + 1R') items[4] = "2*6 1R";
    const gaps = [0, 180, 420, 660, 900];
    items.forEach((text, idx) => {
        ctx.fillText(text, x + gaps[idx], y);
    });
    ctx.restore();
};

const WaveCanvas = ({ waveData, format, lead }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !waveData) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        drawGrid(ctx);

        // 공통 변수
        const topMargin = 50;
        const footerHeight = 100;
        let offsetY = topMargin;

        if (format === '3ch(3ch*2.5s) + 1R') {
            // 3줄(4개씩), 각 채널 2.5초, 마지막 리드 10초(높이 300)
            const tenSecHeight = 300;
            const availableHeight = CANVAS_HEIGHT - tenSecHeight - footerHeight - topMargin;
            const chartHeight = Math.floor((availableHeight - 2) / 3);
            const chartWidth = CANVAS_WIDTH / 4;

            for (let row = 0; row < 3; row++) {
                for (let col = 0; col < 4; col++) {
                    const type = leadOrder_3ch[row][col];
                    const x = col * chartWidth;
                    const y = offsetY;
                    drawWave(
                        ctx,
                        waveData[type]?.wave,
                        x,
                        y,
                        chartWidth,
                        chartHeight,
                        2.5,
                        type
                    );
                    if (col < 3) {
                        drawDivideLine(ctx, x + chartWidth, y, chartHeight);
                    }
                }
                offsetY += chartHeight;
            }
            // 10초 리드(전체폭)
            drawWave(
                ctx,
                waveData[lead]?.wave,
                0,
                offsetY,
                CANVAS_WIDTH,
                tenSecHeight,
                10,
                lead
            );
        }

        if (format === '6ch(6ch*5s)') {
            // 6줄(2개씩), 각 채널 5초, 줄간격 60px, 마지막 리드 없음
            const availableHeight = CANVAS_HEIGHT - footerHeight - topMargin;
            const chartHeight = Math.floor((availableHeight - 5) / 6);
            const chartWidth = CANVAS_WIDTH / 2;

            for (let row = 0; row < 6; row++) {
                for (let col = 0; col < 2; col++) {
                    const type = leadOrder_6ch[row][col];
                    const x = col * chartWidth;
                    const y = offsetY;
                    drawWave(
                        ctx,
                        waveData[type]?.wave,
                        x,
                        y,
                        chartWidth,
                        chartHeight,
                        5,
                        type,
                        20
                    );
                    if (col < 1) {
                        drawDivideLine(ctx, x + chartWidth, y, chartHeight);
                    }
                }
                offsetY += chartHeight ;
            }
        }

        if (format === '6ch(6ch*5s) + 1R') {
            // 6줄(2개씩), 각 채널 5초, 마지막 리드 10초(높이 200)
            const tenSecHeight = 200;
            const availableHeight = CANVAS_HEIGHT - tenSecHeight - footerHeight - topMargin;
            const chartHeight = availableHeight / 6; // 6등분!
            const chartWidth = CANVAS_WIDTH / 2;

            for (let row = 0; row < 6; row++) {
                for (let col = 0; col < 2; col++) {
                    const type = leadOrder_6ch[row][col];
                    const x = col * chartWidth;
                    const y = offsetY;
                    drawWave(
                        ctx,
                        waveData[type]?.wave,
                        x,
                        y,
                        chartWidth,
                        chartHeight,
                        5,
                        type,
                        10
                    );
                    if (col < 1) {
                        drawDivideLine(ctx, x + chartWidth, y, chartHeight);
                    }
                }
                offsetY += chartHeight;
            }
            // 10초 리드(전체폭)
            drawWave(
                ctx,
                waveData[lead]?.wave,
                0,
                offsetY,
                CANVAS_WIDTH,
                tenSecHeight,
                10,
                lead,
                10
            );
        }

        // 하단 텍스트
        drawFooterText(ctx, CANVAS_HEIGHT - footerHeight / 2, format);

    }, [waveData, format, lead]);

    return (
        <div className="flex flex-col items-center justify-center">
            <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                className="block"
            />
        </div>
    );
};

export default WaveCanvas;